import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  Wrench,
  Plus, 
  Trash2, 
  Edit3,
  MapPin,
  Eye,
  Loader2,
  Calendar,
  Hash,
  Building2,
  FileText,
  ExternalLink,
  X,
  Search,
  Filter,
  Package,
  DollarSign
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Equipment type options
const EQUIPMENT_TYPES = [
  { value: 'valve', label: 'Valve' },
  { value: 'pump', label: 'Pump' },
  { value: 'tank', label: 'Tank' },
  { value: 'compressor', label: 'Compressor' },
  { value: 'heat_exchanger', label: 'Heat Exchanger' },
  { value: 'motor', label: 'Motor' },
  { value: 'instrument', label: 'Instrument' },
  { value: 'pipe', label: 'Pipe/Pipeline' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'heater', label: 'Heater/Cata-Dyne' },
  { value: 'other', label: 'Other' },
];

export function EquipmentPanel({ 
  isOpen,
  onClose,
  currentSweep,
  cameraPosition,
  onNavigateToEquipment,
  title = "Assets"
}) {
  const { token, canEdit, isViewer } = useAuth();
  const { currentTour } = useTour();
  const [equipment, setEquipment] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [buildingFilter, setBuildingFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [newEquipment, setNewEquipment] = useState({
    name: '',
    equipment_type: 'valve',
    tag_number: '',
    description: '',
    manufacturer: '',
    model_number: '',
    serial_number: '',
    building_id: '',
    capturePosition: true
  });

  // Fetch buildings for the dropdown
  const fetchBuildings = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBuildings(data);
      }
    } catch (err) {
      console.error('Failed to fetch buildings:', err);
    }
  }, [token, currentTour?.id]);

  // Fetch equipment from backend
  const fetchEquipment = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      let url = `${API_URL}/api/equipment/tour/${currentTour.id}`;
      if (typeFilter !== 'all') {
        url += `?equipment_type=${typeFilter}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setEquipment(data);
      }
    } catch (err) {
      console.error('Failed to fetch equipment:', err);
    } finally {
      setLoading(false);
    }
  }, [token, currentTour?.id, typeFilter]);

  useEffect(() => {
    if (isOpen) {
      fetchEquipment();
      fetchBuildings();
    }
  }, [isOpen, fetchEquipment, fetchBuildings]);

  // Create equipment
  const handleCreateEquipment = async () => {
    if (!newEquipment.name || !currentTour?.id) return;
    
    setCreating(true);
    try {
      const equipmentData = {
        tour_id: currentTour.id,
        name: newEquipment.name,
        equipment_type: newEquipment.equipment_type,
        tag_number: newEquipment.tag_number || null,
        description: newEquipment.description || null,
        manufacturer: newEquipment.manufacturer || null,
        model_number: newEquipment.model_number || null,
        serial_number: newEquipment.serial_number || null,
        building_id: newEquipment.building_id || null,
      };

      // Capture current 3D position if enabled
      if (newEquipment.capturePosition && currentSweep?.id) {
        equipmentData.sweep_id = currentSweep.id;
        if (cameraPosition?.position) {
          equipmentData.position = cameraPosition.position;
        }
        if (cameraPosition?.rotation) {
          equipmentData.rotation = cameraPosition.rotation;
        }
      }

      const res = await fetch(`${API_URL}/api/equipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(equipmentData)
      });

      if (res.ok) {
        const created = await res.json();
        setEquipment(prev => [created, ...prev]);
        setIsCreateDialogOpen(false);
        setNewEquipment({
          name: '',
          equipment_type: 'valve',
          tag_number: '',
          description: '',
          manufacturer: '',
          model_number: '',
          serial_number: '',
          building_id: '',
          capturePosition: true
        });
      }
    } catch (err) {
      console.error('Failed to create equipment:', err);
    } finally {
      setCreating(false);
    }
  };

  // Delete equipment
  const handleDeleteEquipment = async (equipmentId) => {
    try {
      const res = await fetch(`${API_URL}/api/equipment/${equipmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setEquipment(prev => prev.filter(e => e.id !== equipmentId));
        setSelectedEquipment(null);
      }
    } catch (err) {
      console.error('Failed to delete equipment:', err);
    }
  };

  // Navigate to equipment location
  const handleNavigate = (eq) => {
    if (eq.sweep_id && onNavigateToEquipment) {
      setSelectedEquipment(null);
      setTimeout(() => {
        onNavigateToEquipment(eq.sweep_id, { 
          rotation: eq.rotation,
          transitionTime: 1500 
        });
      }, 100);
    }
  };

  // Filter equipment by search query and building
  const filteredEquipment = equipment.filter(eq => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        eq.name.toLowerCase().includes(query) ||
        eq.tag_number?.toLowerCase().includes(query) ||
        eq.equipment_type?.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }
    // Building filter
    if (buildingFilter !== 'all') {
      if (buildingFilter === 'unassigned') {
        if (eq.building_id) return false;
      } else if (eq.building_id !== buildingFilter) {
        return false;
      }
    }
    return true;
  });

  // Get building name by ID
  const getBuildingName = (buildingId) => {
    const building = buildings.find(b => b.id === buildingId);
    return building?.name || 'Unassigned';
  };

  const getEquipmentTypeLabel = (type) => {
    return EQUIPMENT_TYPES.find(t => t.value === type)?.label || type;
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            {title}
            <Badge variant="secondary">{equipment.length}</Badge>
          </SheetTitle>
          <SheetDescription>
            Track and manage assets for {currentTour?.name || 'this tour'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Search and Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search equipment..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[130px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {EQUIPMENT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Building Filter */}
          {buildings.length > 0 && (
            <Select value={buildingFilter} onValueChange={setBuildingFilter}>
              <SelectTrigger className="w-full">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by Building" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Buildings</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {buildings.map(building => (
                  <SelectItem key={building.id} value={building.id}>
                    {building.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Add Equipment Button - Only for admin/client */}
          {canEdit && (
            <Button 
              className="w-full gap-2" 
              onClick={() => setIsCreateDialogOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Add Equipment
            </Button>
          )}

          {/* Viewer mode notice */}
          {isViewer && (
            <div className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded">
              You have view-only access. Contact an admin to add equipment.
            </div>
          )}

          {/* Equipment List */}
          <ScrollArea className="h-[calc(100vh-320px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredEquipment.length > 0 ? (
              <div className="space-y-2 pr-4">
                {filteredEquipment.map((eq) => (
                  <div
                    key={eq.id}
                    className={cn(
                      "p-3 rounded-lg border cursor-pointer transition-all",
                      "hover:border-primary/50 hover:bg-secondary/50",
                      selectedEquipment?.id === eq.id && "border-primary bg-secondary/50"
                    )}
                    onClick={() => setSelectedEquipment(eq)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{eq.name || 'Unnamed Equipment'}</p>
                          <Badge variant="outline" className="text-[10px]">
                            {getEquipmentTypeLabel(eq.equipment_type)}
                          </Badge>
                        </div>
                        {eq.tag_number && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Hash className="h-3 w-3" />
                            {eq.tag_number}
                          </p>
                        )}
                        {eq.building_id && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Building2 className="h-3 w-3" />
                            {getBuildingName(eq.building_id)}
                          </p>
                        )}
                        {eq.sweep_id && (
                          <p className="text-xs text-green-600 mt-1">
                            📍 Position saved
                          </p>
                        )}
                      </div>
                      {eq.sweep_id && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigate(eq);
                          }}
                          title="Go to location"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Wrench className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No equipment registered</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Add Equipment" to get started
                </p>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Equipment Detail Dialog */}
        <Dialog open={!!selectedEquipment} onOpenChange={(open) => !open && setSelectedEquipment(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedEquipment?.name}</DialogTitle>
              <DialogDescription>
                <Badge variant="outline">
                  {getEquipmentTypeLabel(selectedEquipment?.equipment_type)}
                </Badge>
              </DialogDescription>
            </DialogHeader>
            
            {selectedEquipment && (
              <div className="space-y-4 py-4">
                {selectedEquipment.tag_number && (
                  <div className="flex items-center gap-2 text-sm">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Tag:</span>
                    <span className="font-mono">{selectedEquipment.tag_number}</span>
                  </div>
                )}

                {selectedEquipment.building_id && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Building:</span>
                    <span className="font-medium">{getBuildingName(selectedEquipment.building_id)}</span>
                  </div>
                )}
                
                {selectedEquipment.description && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Description</p>
                    <p className="text-sm">{selectedEquipment.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {selectedEquipment.manufacturer && (
                    <div>
                      <p className="text-xs text-muted-foreground">Manufacturer</p>
                      <p className="text-sm font-medium">{selectedEquipment.manufacturer}</p>
                    </div>
                  )}
                  {selectedEquipment.model_number && (
                    <div>
                      <p className="text-xs text-muted-foreground">Model</p>
                      <p className="text-sm font-medium">{selectedEquipment.model_number}</p>
                    </div>
                  )}
                  {selectedEquipment.serial_number && (
                    <div>
                      <p className="text-xs text-muted-foreground">Serial Number</p>
                      <p className="text-sm font-mono">{selectedEquipment.serial_number}</p>
                    </div>
                  )}
                </div>

                {selectedEquipment.sweep_id && (
                  <div className="p-3 rounded-lg bg-green-500/10 text-green-700">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4" />
                      3D Position Saved
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {selectedEquipment.sweep_id && (
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleNavigate(selectedEquipment)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Go to Location
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleDeleteEquipment(selectedEquipment.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Equipment Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Equipment</DialogTitle>
              <DialogDescription>
                Register new equipment in this tour
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="eq-name">Equipment Name *</Label>
                  <Input
                    id="eq-name"
                    placeholder="e.g., Main Feed Pump"
                    value={newEquipment.name}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select 
                    value={newEquipment.equipment_type} 
                    onValueChange={(value) => setNewEquipment(prev => ({ ...prev, equipment_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="eq-tag">Tag Number</Label>
                <Input
                  id="eq-tag"
                  placeholder="e.g., P-101, V-202"
                  value={newEquipment.tag_number}
                  onChange={(e) => setNewEquipment(prev => ({ ...prev, tag_number: e.target.value }))}
                />
              </div>

              {/* Building Selector */}
              <div className="space-y-2">
                <Label>Building</Label>
                <Select 
                  value={newEquipment.building_id || 'none'} 
                  onValueChange={(value) => setNewEquipment(prev => ({ ...prev, building_id: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger>
                    <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Select building (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Building</SelectItem>
                    {buildings.map(building => (
                      <SelectItem key={building.id} value={building.id}>
                        {building.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {buildings.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No buildings created yet. Add buildings first from the Buildings panel.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="eq-desc">Description</Label>
                <Textarea
                  id="eq-desc"
                  placeholder="Brief description of the equipment..."
                  value={newEquipment.description}
                  onChange={(e) => setNewEquipment(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="eq-mfr">Manufacturer</Label>
                  <Input
                    id="eq-mfr"
                    placeholder="e.g., Flowserve"
                    value={newEquipment.manufacturer}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, manufacturer: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eq-model">Model Number</Label>
                  <Input
                    id="eq-model"
                    placeholder="e.g., 3196 STX"
                    value={newEquipment.model_number}
                    onChange={(e) => setNewEquipment(prev => ({ ...prev, model_number: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="eq-serial">Serial Number</Label>
                <Input
                  id="eq-serial"
                  placeholder="e.g., SN-12345678"
                  value={newEquipment.serial_number}
                  onChange={(e) => setNewEquipment(prev => ({ ...prev, serial_number: e.target.value }))}
                />
              </div>

              {/* Link to 3D Position */}
              <div className="p-3 rounded-lg border space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    Link to 3D Position
                  </Label>
                  <Button
                    variant={newEquipment.capturePosition ? "default" : "outline"}
                    size="sm"
                    onClick={() => setNewEquipment(prev => ({ ...prev, capturePosition: !prev.capturePosition }))}
                  >
                    {newEquipment.capturePosition ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
                {newEquipment.capturePosition && currentSweep?.id && (
                  <p className="text-xs text-green-600">
                    ✓ Current position will be saved: {currentSweep.id.substring(0, 12)}...
                  </p>
                )}
                {newEquipment.capturePosition && !currentSweep?.id && (
                  <p className="text-xs text-yellow-600">
                    ⚠️ Navigate in the 3D viewer to capture position
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateEquipment} disabled={!newEquipment.name || creating}>
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Equipment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

export default EquipmentPanel;
